import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { movies, categoryDetails } from '@/mocks/movies';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const movie = movies.find(m => m.id === id);
  const article = articles.find(a => a.id === id);
  const content = movie || article;

  if (!content) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-50 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary-500 text-background-950 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isMovie = 'director' in content;
  const isArticleContent = 'author' in content && 'content' in content;

  const breadcrumbItems = isMovie
    ? [
        { label: '首页', href: '/' },
        { label: content.category, href: `/category/${content.categorySlug}` },
        { label: content.title },
      ]
    : [
        { label: '首页', href: '/' },
        { label: '影视资讯' },
        { label: (content as typeof articles[0]).title },
      ];

  const relatedMovies = isMovie
    ? movies.filter(m => m.id !== content.id && m.categorySlug === content.categorySlug).slice(0, 4)
    : [];

  const articleData = isArticleContent ? (content as typeof articles[0]) : null;
  const relatedArticles = articleData
    ? articles.filter(a => a.id !== articleData.id).slice(0, 3)
    : [];

  const getPageTitle = () => {
    if (isMovie) return `${content.title} - 高清${content.category}免费在线观看 | 久久影视`;
    if (articleData) return `${articleData.title} | 久久影视`;
    return '久久影视';
  };

  const getPageDesc = () => {
    if (isMovie) return `久久影视提供${content.title}高清免费在线观看，${content.description.slice(0, 80)}...高清画质不缓冲，畅享极致观影体验。`;
    if (articleData) return articleData.summary;
    return '';
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type={isMovie ? 'WebPage' : 'Article'}
        data={isMovie ? {
          name: getPageTitle(),
          description: getPageDesc(),
          url: `https://www.jiujiutv.com/article/${id}`,
          isPartOf: { '@type': 'WebSite', name: '久久影视', url: 'https://www.jiujiutv.com' },
        } : {
          headline: articleData?.title,
          description: articleData?.summary,
          datePublished: articleData?.date,
          author: { '@type': 'Person', name: articleData?.author },
          url: `https://www.jiujiutv.com/article/${id}`,
          publisher: { '@type': 'Organization', name: '久久影视' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: breadcrumbItems.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.label,
            item: item.href ? `https://www.jiujiutv.com${item.href}` : `https://www.jiujiutv.com/article/${id}`,
          })),
        }}
      />

      <Navbar />

      <main>
        {isMovie ? (
          <>
            <section className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
              <img
                src={content.poster}
                alt={content.title}
                title={content.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
            </section>

            <section className="py-6 md:py-10 -mt-20 relative z-10">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-4xl mx-auto">
                  <Breadcrumb items={breadcrumbItems} />

                  <div className="bg-background-100 rounded-lg p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                      <div className="w-40 md:w-48 shrink-0 mx-auto md:mx-0">
                        <div className="aspect-[3/4] rounded-lg overflow-hidden">
                          <img
                            src={content.poster}
                            alt={content.title}
                            title={`${content.title}海报 - 久久影视`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {content.isHot && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-accent-500 text-background-50 whitespace-nowrap">
                              热门
                            </span>
                          )}
                          {content.isNew && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-primary-500 text-background-950 whitespace-nowrap">
                              新上
                            </span>
                          )}
                          <span className="text-xs text-foreground-500">{content.year}</span>
                        </div>

                        <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                          {content.title}
                        </h1>

                        <div className="flex items-center gap-4 mb-4 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-star-fill text-primary-500 text-sm"></i>
                            </div>
                            <span className="text-lg font-semibold text-primary-500">{content.rating}</span>
                          </div>
                          <span className="text-sm text-foreground-500">{content.duration}</span>
                          <span className="text-sm text-foreground-500">{content.region}</span>
                        </div>

                        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                          {content.tags.map((tag: string) => (
                            <span
                              key={tag}
                              className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                          {content.description}
                        </p>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-foreground-600">导演：</span>
                            <span className="text-foreground-300">{content.director}</span>
                          </div>
                          <div>
                            <span className="text-foreground-600">主演：</span>
                            <span className="text-foreground-300">{content.actors.join(' / ')}</span>
                          </div>
                          <div>
                            <span className="text-foreground-600">类型：</span>
                            <span className="text-foreground-300">{content.category}</span>
                          </div>
                          <div>
                            <span className="text-foreground-600">更新日期：</span>
                            <span className="text-foreground-300">{content.updateDate}</span>
                          </div>
                        </div>

                        <Link
                          to={`/category/${content.categorySlug}`}
                          className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-950 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-play-circle-line text-sm"></i>
                          </div>
                          观看{content.category}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {relatedMovies.length > 0 && (
              <section className="py-10 md:py-14 bg-background-100">
                <div className="w-full px-4 md:px-6">
                  <div className="max-w-7xl mx-auto">
                    <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6">
                      同类推荐
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                      {relatedMovies.map((m) => (
                        <MovieCard key={m.id} movie={m} />
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        ) : articleData ? (
          <>
            <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
              <img
                src={articleData.image}
                alt={articleData.title}
                title={articleData.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
            </section>

            <section className="py-6 md:py-10 -mt-20 relative z-10">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-3xl mx-auto">
                  <Breadcrumb items={breadcrumbItems} />

                  <article className="bg-background-100 rounded-lg p-6 md:p-10">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="px-2.5 py-0.5 text-xs rounded bg-accent-100 text-accent-900 whitespace-nowrap">
                        {articleData.category}
                      </span>
                      <span className="text-xs text-foreground-600">{articleData.date}</span>
                      <span className="text-xs text-foreground-600">·</span>
                      <span className="text-xs text-foreground-600">{articleData.author}</span>
                    </div>

                    <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-50 mb-4">
                      {articleData.title}
                    </h1>

                    <div className="flex items-center gap-1.5 mb-6 flex-wrap">
                      {articleData.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div
                      className="prose prose-invert max-w-none text-foreground-300 leading-relaxed text-sm md:text-base article-content"
                      dangerouslySetInnerHTML={{
                        __html: articleData.content
                          .replace(/^### (.+)$/gm, '<h3 class="text-lg font-heading font-semibold text-foreground-50 mt-6 mb-3">$1</h3>')
                          .replace(/^## (.+)$/gm, '<h2 class="text-xl font-heading font-bold text-foreground-50 mt-8 mb-4">$1</h2>')
                          .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed">')
                          .replace(/^(.+)$/gm, (match: string) => {
                            if (match.startsWith('<h') || match.startsWith('</p>')) return match;
                            return `<p class="mb-4 leading-relaxed">${match}</p>`;
                          }),
                      }}
                    />
                  </article>

                  {relatedArticles.length > 0 && (
                    <div className="mt-10">
                      <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-5">
                        相关文章
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {relatedArticles.map((ra) => (
                          <Link
                            key={ra.id}
                            to={`/article/${ra.id}`}
                            className="group cursor-pointer rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-colors duration-200"
                          >
                            <div className="aspect-[16/9] overflow-hidden">
                              <img
                                src={ra.image}
                                alt={ra.title}
                                title={ra.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                              />
                            </div>
                            <div className="p-4">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-accent-100 text-accent-900 whitespace-nowrap">
                                {ra.category}
                              </span>
                              <h3 className="text-sm font-heading font-medium text-foreground-50 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mt-2">
                                {ra.title}
                              </h3>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-8 text-center">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-background-200 text-foreground-400 text-sm hover:text-foreground-50 hover:bg-background-300 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line text-xs"></i>
                      </div>
                      返回首页
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </main>

      <Footer />
    </>
  );
}